/**
 * Pokémon Evolution & Theme – Utility Functions
 *
 * Provides:
 *  - matchPokemonToFamily
 *  - getFamiliesFromInventory
 *  - getRecommendedBundles
 *  - getNearlyCompleteLines
 *
 * No O(n²) scans; all name lookups are O(1) via pre-built Maps.
 * Each card is attributed to a single primary family (highest priority,
 * ties broken deterministically by familyId) so a Pokémon shared by
 * multiple families is never double-counted across competing bundles.
 */

import type { Card } from "@/context/AppContext";
import {
  EVOLUTION_FAMILIES,
  FAMILY_BY_ID,
  NAME_TO_FAMILY_IDS,
  normalizeName,
  type EvolutionFamily,
} from "@/constants/pokemonThemeMaps";

export interface FamilyMatch {
  family: EvolutionFamily;
  matchedCards: Card[];
  /** Number of distinct Pokémon species matched in this family */
  uniquePokemonCount: number;
  /** Total cards (sum of card.quantity) */
  totalCardCount: number;
  /**
   * true if every member of family.coreLine (or family.pokemon when
   * coreLine is absent) is represented by at least one card.
   */
  hasFullLine: boolean;
}

export interface BundleRecommendation {
  title: string;
  familyId: string;
  cardIds: string[];
  cardCount: number;
  themeType: EvolutionFamily["themeType"];
  hasFullLine: boolean;
  /** Computed score – higher is better */
  score: number;
}

export interface RecommendationConfig {
  /** Minimum total cards to surface a bundle recommendation */
  minCardCount: number;
  /** Always surface if full line is present, even below minCardCount */
  alwaysSurfaceFullLine: boolean;
}

const DEFAULT_CONFIG: RecommendationConfig = {
  minCardCount: 10,
  alwaysSurfaceFullLine: true,
};

export interface CompletionStatus {
  family: EvolutionFamily;
  presentPokemon: string[];
  missingPokemon: string[];
  completionPercent: number;
  totalCardCount: number;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Restrict the inventory to cards that are realistically bundle-eligible:
 * unlisted and still in stock. Sold / listed / in-package cards must never
 * make their way into a suggested bundle.
 */
function filterBundleableCards(cards: Card[]): Card[] {
  return cards.filter((c) => c.listingStatus === "Unlisted" && c.quantity > 0);
}

/**
 * Compare two families and return the one that should "win" attribution.
 * Higher priority wins; ties broken deterministically by familyId so the
 * same card always lands in the same primary family across runs.
 */
function pickPrimary(a: EvolutionFamily, b: EvolutionFamily): EvolutionFamily {
  const pa = a.priority ?? 0;
  const pb = b.priority ?? 0;
  if (pa !== pb) return pa > pb ? a : b;
  return a.familyId < b.familyId ? a : b;
}

// ---------------------------------------------------------------------------
// 1. matchPokemonToFamily
// ---------------------------------------------------------------------------

/**
 * Given a Pokémon name, returns the highest-priority EvolutionFamily it
 * belongs to (deterministically tie-broken by familyId), or null if the
 * name is unrecognised.
 */
export function matchPokemonToFamily(name: string): EvolutionFamily | null {
  const key = normalizeName(name);
  const familyIds = NAME_TO_FAMILY_IDS.get(key);
  if (!familyIds || familyIds.length === 0) return null;

  let best: EvolutionFamily | null = null;
  for (const id of familyIds) {
    const family = FAMILY_BY_ID.get(id);
    if (!family) continue;
    best = best ? pickPrimary(best, family) : family;
  }
  return best;
}

// ---------------------------------------------------------------------------
// 2. getFamiliesFromInventory
// ---------------------------------------------------------------------------

/**
 * Main entry point. Given a list of Card objects, returns one FamilyMatch
 * per family represented in the inventory, sorted by score descending.
 *
 * Each card is attributed to exactly one family — its highest-priority
 * match — so shared Pokémon (Pichu, Riolu, Mew, Kanto starters, etc.)
 * never appear in two competing bundles.
 *
 * Performance: O(n) card scan + O(1) name lookups. Safe for 10k+ cards.
 */
export function getFamiliesFromInventory(cards: Card[]): FamilyMatch[] {
  const eligible = filterBundleableCards(cards);

  // familyId → bucket keyed by normalized pokémon name
  const accumulator = new Map<
    string,
    { cardsByPokemon: Map<string, Card[]> }
  >();

  for (const card of eligible) {
    const primary = matchPokemonToFamily(card.name);
    if (!primary) continue;

    let bucket = accumulator.get(primary.familyId);
    if (!bucket) {
      bucket = { cardsByPokemon: new Map() };
      accumulator.set(primary.familyId, bucket);
    }
    const nameKey = normalizeName(card.name);
    const existing = bucket.cardsByPokemon.get(nameKey) ?? [];
    existing.push(card);
    bucket.cardsByPokemon.set(nameKey, existing);
  }

  const results: FamilyMatch[] = [];

  for (const [fid, { cardsByPokemon }] of accumulator) {
    const family = FAMILY_BY_ID.get(fid);
    if (!family) continue;

    const matchedCards: Card[] = [];
    for (const list of cardsByPokemon.values()) matchedCards.push(...list);

    const totalCardCount = matchedCards.reduce(
      (sum, c) => sum + (c.quantity ?? 1),
      0,
    );
    const uniquePokemonCount = cardsByPokemon.size;

    const lineToCheck = family.coreLine ?? family.pokemon;
    const hasFullLine = lineToCheck.every((p) =>
      cardsByPokemon.has(normalizeName(p)),
    );

    results.push({
      family,
      matchedCards,
      uniquePokemonCount,
      totalCardCount,
      hasFullLine,
    });
  }

  return results.sort((a, b) => scoreMatch(b) - scoreMatch(a));
}

// ---------------------------------------------------------------------------
// 3. Internal scoring
// ---------------------------------------------------------------------------

/**
 * Scores a FamilyMatch for ranking purposes. Higher is better.
 *
 * Factors:
 *  - priority          weight × 10
 *  - hasFullLine       +50 bonus
 *  - totalCardCount    linear (capped at 100 to avoid domination)
 *  - uniquePokemon     +5 per distinct species
 */
function scoreMatch(match: FamilyMatch): number {
  const { family, hasFullLine, totalCardCount, uniquePokemonCount } = match;
  const priorityScore = (family.priority ?? 5) * 10;
  const fullLineBonus = hasFullLine ? 50 : 0;
  const cardScore = Math.min(totalCardCount, 100);
  const speciesScore = uniquePokemonCount * 5;
  return priorityScore + fullLineBonus + cardScore + speciesScore;
}

// ---------------------------------------------------------------------------
// 4. getRecommendedBundles
// ---------------------------------------------------------------------------

export function getRecommendedBundles(
  cards: Card[],
  config: Partial<RecommendationConfig> = {},
): BundleRecommendation[] {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const matches = getFamiliesFromInventory(cards);

  return matches
    .filter((m) => {
      if (cfg.alwaysSurfaceFullLine && m.hasFullLine) return true;
      return m.totalCardCount >= cfg.minCardCount;
    })
    .map((m) => buildBundleRecommendation(m));
}

function buildBundleRecommendation(match: FamilyMatch): BundleRecommendation {
  const { family, matchedCards, totalCardCount, hasFullLine } = match;

  const title = hasFullLine
    ? `${family.label} – Complete Bundle`
    : `${family.label} Bundle`;

  return {
    title,
    familyId: family.familyId,
    cardIds: matchedCards.map((c) => c.id),
    cardCount: totalCardCount,
    themeType: family.themeType,
    hasFullLine,
    score: scoreMatch(match),
  };
}

// ---------------------------------------------------------------------------
// 5. getNearlyCompleteLines  ("you're close – consider bundling" UX)
// ---------------------------------------------------------------------------

/**
 * Returns families where the inventory has ≥ `threshold` of the core line
 * (default 0.5) but is NOT yet complete.
 */
export function getNearlyCompleteLines(
  cards: Card[],
  threshold = 0.5,
): CompletionStatus[] {
  const matches = getFamiliesFromInventory(cards);
  const results: CompletionStatus[] = [];

  for (const match of matches) {
    if (match.hasFullLine) continue;

    const { family } = match;
    const lineToCheck = family.coreLine ?? family.pokemon;
    const presentSet = new Set(
      match.matchedCards.map((c) => normalizeName(c.name)),
    );

    const presentPokemon = lineToCheck.filter((p) =>
      presentSet.has(normalizeName(p)),
    );
    const missingPokemon = lineToCheck.filter(
      (p) => !presentSet.has(normalizeName(p)),
    );
    const completionPercent = presentPokemon.length / lineToCheck.length;

    if (completionPercent >= threshold) {
      results.push({
        family,
        presentPokemon,
        missingPokemon,
        completionPercent,
        totalCardCount: match.totalCardCount,
      });
    }
  }

  return results.sort((a, b) => b.completionPercent - a.completionPercent);
}

// ---------------------------------------------------------------------------
// Re-exports for convenience
// ---------------------------------------------------------------------------

export { EVOLUTION_FAMILIES, FAMILY_BY_ID, NAME_TO_FAMILY_IDS, normalizeName };
export type { EvolutionFamily };
